
// Updated UserPaymentModalHistory.js with enhanced columns and better pagination
import React, { useState, useEffect } from 'react';
import { baseUrl } from "../../config";
import { Link } from "react-router-dom";
import moment from 'moment';
import { TableProps } from 'antd';
import axios from "axios";
import { getPermissions, hasPermission } from '../../components/permissions';

import {
  Row,
  Col,
  Card,
  Button,
  List,
  Descriptions,
  Avatar,
  Radio,
  Switch,
  Upload,
  Space,
  Table,
  Tag,
  message, 
  notification, 
  Select, 
  Typography, 
  Form,
  Spin,
  Empty,
  Tooltip
} from "antd";

import {
  FacebookOutlined,
  TwitterOutlined,
  InstagramOutlined,
  VerticalAlignTopOutlined, 
  EditOutlined,
  ReloadOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  BankOutlined,
  FileTextOutlined
} from "@ant-design/icons";

const { Title, Text } = Typography;

const UserPaymentModalHistory = ({ userid }) => {
  const [paymentData, setPaymentData] = useState([]);
  const [UserPaymentDetail, setUserPaymentDetail] = useState(false);
  const [UserPaymentOther, setUserPaymentOther] = useState({});
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    pageSizeOptions: ['10', '20', '50', '100'],
  });

  const [accountFilter, setAccountFilter] = useState(null); // null = all accounts

  useEffect(() => {
    if (userid) {
      setLoading(true);
      Promise.all([
        UserPaymentOverView(),
        GetUserPaymentOther(),
        fetchPaymentData(1, 10)
      ]).finally(() => {
        setLoading(false);
      });
    }
  }, [userid]);

  // Enhanced columns with more details
  const columns = [
    {
      title: 'SR. No',
      dataIndex: 'srNo',
      key: 'srNo',
      fixed: 'left',
      width: 60,
      align: 'center',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (text) => (
        <span style={{ fontWeight: 'bold', color: '#7a0000' }}>
          ₹ {text?.toLocaleString() || '0'}
        </span>
      ),
      fixed: 'left',
      width: 120,
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
    },
    {
      title: 'Account & Category Details',
      key: 'account_details',
      width: 280,
      ellipsis: true,
      render: (_, record) => (
        <div style={{ lineHeight: '1.4' }}>
          <div style={{ marginBottom: '4px' }}>
            <BankOutlined style={{ color: '#7a0000', marginRight: '6px' }} />
            <strong>Account:</strong> {record.bank_account || '-'}
          </div>
          <div style={{ marginBottom: '4px' }}>
            <FileTextOutlined style={{ color: '#52c41a', marginRight: '6px' }} />
            <strong>Category:</strong> {record.special_occassion_category || 'Regular Payment'}
          </div>
          {record.utr_number && (
            <div>
              <strong>UTR:</strong> <Text code copyable>{record.utr_number}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Payment Period',
      key: 'payment_period',
      width: 180,
      render: (_, record) => (
        <div style={{ lineHeight: '1.4' }}>
          <div style={{ marginBottom: '4px' }}>
            <strong>Start:</strong> {record.start_month ? moment(record.start_month).format('MMM-YYYY') : '-'}
          </div>
          <div style={{ marginBottom: '4px' }}>
            <strong>End:</strong> {record.end_month ? moment(record.end_month).format('MMM-YYYY') : '-'}
          </div>
          {record.frequency_type && record.frequency_type.trim() !== '' && (
            <div>
              <Tag size="small" color="blue">
                {record.frequency_type}
              </Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Transaction Date',
      dataIndex: 'transaction_date',
      key: 'transaction_date',
      width: 140,
      render: (text) => (
        <div style={{ textAlign: 'center' }}>
          <CalendarOutlined style={{ color: '#faad14', marginBottom: '4px', display: 'block' }} />
          <Text strong>{text ? moment(text).format('DD-MMM-YYYY') : '-'}</Text>
        </div>
      ),
      sorter: (a, b) => moment(a.transaction_date).valueOf() - moment(b.transaction_date).valueOf(),
    },
    {
      title: 'Entry Date',
      dataIndex: 'payment_entry_date',
      key: 'payment_entry_date',
      width: 140,
      render: (text) => (
        <div style={{ textAlign: 'center' }}>
          <Text type="secondary">
            {text ? moment(text).format('DD-MMM-YYYY') : '-'}
          </Text>
        </div>
      ),
      sorter: (a, b) => moment(a.payment_entry_date).valueOf() - moment(b.payment_entry_date).valueOf(),
    },
    {
      title: 'Payment Details',
      key: 'payment_details',
      width: 200,
      render: (_, record) => (
        <div style={{ lineHeight: '1.6' }}>
          <div><strong>Mode:</strong> {record.pyament_mode_type || '-'}</div>
          {record.receipt_no && (
            <div><strong>Receipt No:</strong> <Text code style={{ fontSize: 11 }}>{record.receipt_no}</Text></div>
          )}
          {record.e_reciept_no && (
            <div><strong>Manual Receipt:</strong> <Text code style={{ fontSize: 11 }}>{record.e_reciept_no}</Text></div>
          )}
          {record.remarks && (
            <Tooltip title={record.remarks}>
              <div style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
                {record.remarks}
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Verification Status',
      dataIndex: 'payment_virify',
      key: 'payment_virify',
      width: 130,
      align: 'center',
      render: (text) => {
        const statusConfig = {
          verified: { color: 'green', text: 'VERIFIED' },
          failed: { color: 'red', text: 'FAILED' },
          pending: { color: 'orange', text: 'PENDING' },
          approved: { color: 'blue', text: 'APPROVED' },
          rejected: { color: 'volcano', text: 'REJECTED' }
        };
        const config = statusConfig[text?.toLowerCase()] || { color: 'default', text: text?.toUpperCase() || 'UNKNOWN' };
        return (
          <Tag color={config.color} style={{ fontWeight: 'bold', minWidth: '80px', textAlign: 'center' }}>
            {config.text}
          </Tag>
        );
      },
      filters: [
        { text: 'Verified', value: 'verified' },
        { text: 'Pending', value: 'pending' },
        { text: 'Failed', value: 'failed' },
        { text: 'Rejected', value: 'rejected' },
      ],
      onFilter: (value, record) => record.payment_virify?.toLowerCase() === value,
    },
  ];

  const UserPaymentOverView = async () => {
    try {
      const response = await axios.get(`${baseUrl}/payments/UserPaymentOverView/${userid}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.status === 200) {  
        const { PaymentSN } = response.data.result;
        setUserPaymentDetail(PaymentSN);  
      }  
    } catch (error) {
      console.error("API error:", error);
      notification.error({
        message: 'Error',
        description: 'Failed to load payment overview',
        placement: 'topRight'
      });
    }
  };

  const GetUserPaymentOther = async () => {
    try {
      const response = await axios.get(`${baseUrl}/payments/UserPaymentOther/${userid}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.status === 200) {  
        const { groupedPayments } = response.data.result; 
        setUserPaymentOther(groupedPayments);  
      }  
    } catch (error) {
      console.error("API error:", error);
      // Silently fail for other payments as it's optional
    }
  };

  const fetchPaymentData = async (page = 1, pageSize = 10, bankAccount = null) => {
    try {
      setTableLoading(true);
      const acct = bankAccount !== undefined ? bankAccount : accountFilter;
      const accountParam = acct ? `&bank_account=${acct}` : '';
      const response = await axios.get(
        `${baseUrl}/payments/latestUserPayment/${userid}?page=${page}&limit=${pageSize}${accountParam}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
      );

      if (response.status === 200) {
        const resultData = response.data.result || [];
        const totalCount = response.data.total || 0;
        const indexedData = resultData.map((item, index) => ({
          ...item,
          srNo: (page - 1) * pageSize + index + 1,
          key: item.payment_id || `payment-${index}`
        }));
        setPaymentData(indexedData);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: pageSize,
          total: totalCount,
        }));
      }
    } catch (error) {
      console.error("API error:", error);
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to load payment data',
        placement: 'topRight'
      });
      setPaymentData([]);
    } finally {
      setTableLoading(false);
    }
  };

  const handleTableChange = (paginationConfig) => {
    const { current, pageSize } = paginationConfig;
    fetchPaymentData(current, pageSize, accountFilter);
  };

  const refreshData = () => {
    fetchPaymentData(pagination.current, pagination.pageSize);
    UserPaymentOverView();
    GetUserPaymentOther();
  };

  const { total_pledge_amount, received_amount } = UserPaymentDetail || {};

  const renderDueOrAdvanceAmount = () => {
    if (UserPaymentDetail?.payment_commitment === 'one_time') return null;
    if (!total_pledge_amount || !received_amount) return null;

    if (received_amount > total_pledge_amount) {
      const advanceAmount = received_amount - total_pledge_amount;
      return (
        <Descriptions.Item label="Advance Amount">
          <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
            ₹ {Number(advanceAmount).toLocaleString('en-IN')}
          </span>
        </Descriptions.Item>
      );
    } else {
      const dueAmount = total_pledge_amount - received_amount;
      return (
        <Descriptions.Item label="Due Amount">
          <span style={{ color: dueAmount > 0 ? '#ff4d4f' : '#52c41a', fontWeight: 'bold' }}>
            {dueAmount > 0 ? `₹ ${Number(dueAmount).toLocaleString('en-IN')}` : 'Nil'}
          </span>
        </Descriptions.Item>
      );
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading payment data...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      <Row gutter={[4, 4]}>
        {/* Payment Overview Section */}
        <Col span={24}>
          <Card
            title={<span style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '1.5px' }}>Payment Overview</span>}
            styles={{ body: { padding: '4px 8px' } }}
          >
            {UserPaymentDetail ? (
              <>
                {UserPaymentDetail.payment_commitment === 'one_time' && (
                  <div style={{ marginBottom: 8, padding: '6px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag color="green" style={{ fontWeight: 700, fontSize: 12, margin: 0 }}>One-Time Donor</Tag>
                    <span style={{ fontSize: 12, color: '#389e0d' }}>This member made a one-time contribution — no monthly pending applicable.</span>
                  </div>
                )}
                <Descriptions
                  size="small"
                  bordered
                  column={{ xs: 1, sm: 2, md: 3 }}
                  labelStyle={{ fontWeight: 'bold', width: '140px', padding: '2px 6px', fontSize: 12 }}
                  contentStyle={{ padding: '2px 6px', fontSize: 12 }}
                >
                  <Descriptions.Item span={3} label="Account">
                    <span style={{ fontWeight: 'bold', fontSize: 13 }}>{UserPaymentDetail.bank_account}</span>
                  </Descriptions.Item>

                  {/* Row 1 */}
                  <Descriptions.Item label="Joining Date">
                    {UserPaymentDetail.joining_date ? moment(UserPaymentDetail.joining_date).format('DD-MMM-YYYY') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment Type">
                    <Tag color={UserPaymentDetail.payment_commitment === 'one_time' ? 'green' : 'blue'} style={{ fontWeight: 700 }}>
                      {UserPaymentDetail.payment_commitment === 'one_time' ? 'One-Time' : 'Monthly (Recurring)'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Member Status">
                    <Tag color={UserPaymentDetail.status?.toLowerCase() === 'active' ? 'green' : 'red'}>
                      {UserPaymentDetail.status || '-'}
                    </Tag>
                  </Descriptions.Item>

                  {/* Row 2 */}
                  <Descriptions.Item label="First Payment Date">
                    {UserPaymentDetail.first_payment_date ? moment(UserPaymentDetail.first_payment_date).format('DD-MMM-YYYY') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Duration (Months)">
                    <span style={{ fontWeight: 700 }}>{UserPaymentDetail.total_duration || '-'}</span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Announced for Year">
                    {UserPaymentDetail.announced_for_year || '-'}
                  </Descriptions.Item>

                  {/* Row 3 — only for recurring */}
                  {UserPaymentDetail.payment_commitment !== 'one_time' && (
                    <Descriptions.Item label="Monthly Share">
                      <span style={{ fontWeight: 700 }}>₹ {Number(UserPaymentDetail.announced_amount || 0).toLocaleString('en-IN')}</span>
                      {UserPaymentDetail.initial_announced_amount &&
                       Number(UserPaymentDetail.initial_announced_amount) !== Number(UserPaymentDetail.announced_amount) && (
                        <span style={{ fontSize: 11, color: '#8c8c8c', marginLeft: 8 }}>
                          (joined ₹{Number(UserPaymentDetail.initial_announced_amount).toLocaleString('en-IN')}
                          {UserPaymentDetail.announced_amount_updated_at
                            ? `, changed ${moment(UserPaymentDetail.announced_amount_updated_at).format('MMM YYYY')}`
                            : ''})
                        </span>
                      )}
                    </Descriptions.Item>
                  )}
                  {UserPaymentDetail.payment_commitment !== 'one_time' && (
                    <Descriptions.Item label="Amount Pledge">
                      {UserPaymentDetail.total_pledge_amount
                        ? <span style={{ fontWeight: 700 }}>₹ {Number(UserPaymentDetail.total_pledge_amount).toLocaleString('en-IN')}</span>
                        : '-'}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Received Amount">
                    <span style={{ color: '#52c41a', fontWeight: 'bold' }}>₹ {Number(UserPaymentDetail.received_amount || 0).toLocaleString('en-IN')}</span>
                  </Descriptions.Item>

                  {/* Row 4 */}
                  <Descriptions.Item label="Remark">{UserPaymentDetail.remarks || '-'}</Descriptions.Item>
                  {UserPaymentDetail.payment_commitment !== 'one_time' && renderDueOrAdvanceAmount()}
                </Descriptions>
              </>
            ) : (
              <Empty description="No payment overview data available" />
            )}
          </Card>
        </Col>

        {/* Other Payments Section */}
        {Object.keys(UserPaymentOther).length > 0 && (
          <Col span={24}>
            <Card
              title={<Space><span style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '1.5px' }}>Other Payments</span></Space>}
              style={{ borderRadius: '8px', border: '1px solid #e8e8e8' }}
              styles={{ body: { padding: '6px 8px' } }}
            >
              <Row gutter={[8, 6]}>
                {Object.keys(UserPaymentOther).map((accountId) => {
                  const acct = UserPaymentOther[accountId];
                  return (
                    <Col xs={24} sm={12} key={accountId}>
                      <Card
                        size="small"
                        type="inner"
                        title={<Space size={4}><BankOutlined style={{ color: '#7a0000', fontSize: 11 }} /><span style={{ fontWeight: 'bold', color: '#7a0000', fontSize: 12 }}>{acct.bank_account_name}</span></Space>}
                        style={{ borderRadius: '6px', border: '1px solid #d9d9d9' }}
                        styles={{ body: { padding: '6px 10px' } }}
                      >
                        <div style={{ textAlign: 'center', paddingBottom: 6, borderBottom: '1px solid #f0f0f0', marginBottom: 6 }}>
                          <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Total Received</Text>
                          <Text strong style={{ fontSize: 16, color: '#52c41a', lineHeight: 1.2 }}>
                            ₹ {(acct.verified_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </Text>
                          {acct.pending_amount > 0 && (
                            <Tag color="orange" style={{ fontSize: 10, marginTop: 2 }}>
                              ₹ {acct.pending_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })} Pending
                            </Tag>
                          )}
                        </div>
                        <Row gutter={[0, 4]}>
                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Transactions</Text>
                            <Text strong style={{ fontSize: 12 }}>
                              {acct.txn_count || 0}
                              {acct.pending_count > 0 && <Text type="secondary" style={{ fontSize: 10, marginLeft: 3 }}>({acct.verified_count}v/{acct.pending_count}p)</Text>}
                            </Text>
                          </Col>
                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>First Payment</Text>
                            <Text strong style={{ fontSize: 12 }}>{acct.first_payment_date ? moment(acct.first_payment_date).format('DD-MMM-YYYY') : '-'}</Text>
                          </Col>
                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Last Payment</Text>
                            <Text strong style={{ fontSize: 12 }}>{acct.last_payment_date ? moment(acct.last_payment_date).format('DD-MMM-YYYY') : '-'}</Text>
                          </Col>
                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>Receipt No(s)</Text>
                            <Text strong style={{ fontSize: 11, wordBreak: 'break-all' }}>{acct.receipt_nos || '-'}</Text>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Card>
          </Col>
        )}

        {/* Payment History Table */}
        <Col span={24}>
          <Card
            styles={{ body: { padding: '4px 8px' } }}
            title={
              <Space>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Payment History
                </span>
                <Select
                  size="small"
                  placeholder="All Accounts"
                  allowClear
                  style={{ width: 200, fontWeight: 'normal', fontSize: 12 }}
                  value={accountFilter}
                  onChange={(val) => {
                    setAccountFilter(val || null);
                    fetchPaymentData(1, pagination.pageSize, val || null);
                  }}
                >
                  {/* Build unique account list from UserPaymentOther + always include account 1 */}
                  {[
                    ...(UserPaymentDetail ? [{ id: 1, name: UserPaymentDetail.bank_account }] : []),
                    ...Object.entries(UserPaymentOther).map(([id, acct]) => ({ id: Number(id), name: acct.bank_account_name }))
                  ].map(a => (
                    <Select.Option key={a.id} value={a.id}>{a.name}</Select.Option>
                  ))}
                </Select>
              </Space>
            }
            styles={{ body: { padding: '4px 8px' } }}
            extra={
              <Space>
                <span style={{ fontSize: '13px', color: '#666' }}>
                  Total Records: {pagination.total}
                </span>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={refreshData}
                  size="small"
                  style={{ borderColor: '#7a0000', color: '#7a0000', borderRadius: 6 }}
                >
                  Refresh
                </Button>
              </Space>
            }
          >
            <Table
              columns={columns}
              dataSource={paymentData}
              loading={tableLoading}
              pagination={pagination}
              onChange={handleTableChange}
              scroll={{ x: 1100, y: 380 }}
              bordered
              size="small"
              locale={{ emptyText: <Empty description="No payment records found" /> }}
              rowClassName={(_, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
            />
          </Card>
        </Col>
      </Row>

      <style jsx>{`
        .table-row-light {
          background-color: #fafafa;
        }
        .table-row-dark {
          background-color: #ffffff;
        }
        .ant-descriptions-item-label {
          min-width: 150px;
        }
        .ant-table-thead > tr > th {
          background-color: #f0f2f5;
          font-weight: bold;
        }
        .ant-table-tbody > tr > td {
          padding: 8px 12px;
        }
        .ant-table-thead > tr > th {
          padding: 12px;
        }
      `}</style>
    </div>
  );
};

export default UserPaymentModalHistory;